import { useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { Client } from '@microsoft/microsoft-graph-client';
import { loginRequest, functionRequest } from '../authConfig';
import { createGraphClient } from '../services/graphService';

/**
 * Hook pour obtenir un client Graph authentifié
 * Gère l'acquisition silencieuse du token et le fallback popup
 */
export function useGraphClient() {
  const { instance, accounts } = useMsal();
  const [error, setError] = useState<string | null>(null);

  /** Acquiert un token et retourne un client Graph prêt */
  const getClient = useCallback(async (): Promise<Client> => {
    try {
      const account = accounts[0];
      if (!account) throw new Error('Aucun compte connecté');

      // Tenter l'acquisition silencieuse (depuis le cache)
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });

      // Diagnostic: log scopes présents dans le token
      try {
        const payload = JSON.parse(atob(response.accessToken.split('.')[1]));
        console.log('[Token] scp:', payload.scp);
        console.log('[Token] roles:', payload.roles);
      } catch {}

      setError(null);
      return createGraphClient(response.accessToken);
    } catch (silentError) {
      // Si le token a expiré, fallback sur la popup
      try {
        const response = await instance.acquireTokenPopup(loginRequest);
        setError(null);
        return createGraphClient(response.accessToken);
      } catch (popupError: any) {
        setError(popupError.message);
        throw popupError;
      }
    }
  }, [instance, accounts]);

  /**
   * Acquiert le token destiné à la Function Exchange.
   * Audience api://<clientId> et non graph.microsoft.com : c'est ce token que
   * Easy Auth valide côté Function App. Le scope est pré-autorisé sur
   * l'App Registration, donc aucune popup de consentement n'apparaît.
   */
  const getFunctionToken = useCallback(async (): Promise<string> => {
    const account = accounts[0];
    if (!account) throw new Error('Aucun compte connecté');

    try {
      const response = await instance.acquireTokenSilent({
        ...functionRequest,
        account,
      });
      return response.accessToken;
    } catch {
      const response = await instance.acquireTokenPopup(functionRequest);
      return response.accessToken;
    }
  }, [instance, accounts]);

  return { getClient, getFunctionToken, error };
}
